import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Sprout, Calendar, ClipboardList, Wheat, Plus, Loader2, Trash2, Check, X, Droplets, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import GardenPlanner from './GardenPlanner';
import SeasonPlanner from './SeasonPlanner';
import { CropPicker } from '../ui/CropPicker';
import { todayLocal, formatDateShort } from '../../utils/dates';
import { validateQuantity, validateDate } from '../../utils/validation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(', ');
  return err?.message || fallback;
};

const EVENT_TYPES = [
  { value: 'watering', label: 'Watering' },
  { value: 'fertilizer', label: 'Fertilizer' },
  { value: 'pest_treatment', label: 'Pest treatment' },
  { value: 'weeding', label: 'Weeding' },
  { value: 'pruning', label: 'Pruning' },
  { value: 'transplant', label: 'Transplant' },
  { value: 'direct_seed', label: 'Direct seed' },
  { value: 'cover_applied', label: 'Cover applied' },
  { value: 'other', label: 'Other' },
];

const HARVEST_UNITS = ['lbs', 'oz', 'count', 'bunches', 'cups'];

const HARVEST_UNIT_CAPS = { lbs: 500, oz: 8000, count: 10000, bunches: 200, cups: 200 };

export default function GardenPage() {
  const [activeTab, setActiveTab] = useState('manage');
  return (
    <div className="space-y-6 animate-fade-in w-full" data-testid="garden-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" data-testid="garden-page-title">
          <Sprout className="w-7 h-7 text-primary" />
          Garden Manager
        </h1>
        <p className="text-muted-foreground mt-1">Beds, planting plan, care log, and harvests</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 mb-4 w-full" data-testid="garden-subtabs">
          <TabsTrigger value="manage" data-testid="garden-manage-tab">
            <Sprout className="w-4 h-4 mr-1.5" /> Manage
          </TabsTrigger>
          <TabsTrigger value="planner" data-testid="garden-planner-tab">
            <Calendar className="w-4 h-4 mr-1.5" /> Planner
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="garden-log-tab">
            <ClipboardList className="w-4 h-4 mr-1.5" /> Log
          </TabsTrigger>
          <TabsTrigger value="harvest" data-testid="garden-harvest-tab">
            <Wheat className="w-4 h-4 mr-1.5" /> Harvest
          </TabsTrigger>
        </TabsList>
        <TabsContent value="manage"><ManageTab /></TabsContent>
        <TabsContent value="planner"><SeasonPlanner /></TabsContent>
        <TabsContent value="log"><LogTab /></TabsContent>
        <TabsContent value="harvest"><HarvestTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Shared helpers ----------------
function useAuthHeaders() {
  const { token } = useAuth();
  return useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);
}

async function fetchBeds(getHeaders) {
  try {
    const res = await axios.get(`${API_URL}/api/garden/beds`, { headers: getHeaders() });
    return res.data?.beds || [];
  } catch { return []; }
}

async function fetchCrops(getHeaders) {
  try {
    const res = await axios.get(`${API_URL}/api/planner/crops`, { headers: getHeaders() });
    return res.data?.crops || [];
  } catch { return []; }
}

// ---------------- MANAGE TAB ----------------
// Wraps the existing 2D GardenPlanner (drag/drop bed layout) + a bed list with
// "Watered today" one-tap actions. Bed records here are logical care units,
// separate from the 2D layout. Both surfaces share the same page so users see
// their beds in visual + list form on the same tab.
function ManageTab() {
  const getHeaders = useAuthHeaders();
  const [beds, setBeds] = useState([]);
  const [wateredToday, setWateredToday] = useState(new Set()); // Set of bed_ids
  const [editingBed, setEditingBed] = useState(null);
  const [showCreateBed, setShowCreateBed] = useState(false);
  const [bedForm, setBedForm] = useState({ name: '', crop_types_str: '', default_water_amount: '', notes: '', size_sqft: '' });

  const load = useCallback(async () => {
    const [bs, evsRes] = await Promise.all([
      fetchBeds(getHeaders),
      axios.get(`${API_URL}/api/garden/care-events/all?days=2`, { headers: getHeaders() }).catch(() => ({ data: { events: [] } })),
    ]);
    setBeds(bs);
    const today = todayLocal();
    const set = new Set(
      (evsRes.data?.events || [])
        .filter((e) => e.event_type === 'watering' && e.date === today && e.bed_id)
        .map((e) => e.bed_id),
    );
    setWateredToday(set);
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (bed) => {
    setEditingBed(bed);
    setBedForm({
      name: bed.name || '',
      crop_types_str: (bed.crop_types || []).join(', '),
      default_water_amount: bed.default_water_amount || '',
      notes: bed.notes || '',
      size_sqft: bed.size_sqft ?? '',
    });
    setShowCreateBed(true);
  };

  const openCreate = () => {
    setEditingBed(null);
    setBedForm({ name: '', crop_types_str: '', default_water_amount: '', notes: '', size_sqft: '' });
    setShowCreateBed(true);
  };

  const saveBed = async () => {
    if (!bedForm.name.trim()) { toast.error('Bed name is required'); return; }
    const payload = {
      name: bedForm.name.trim(),
      crop_types: bedForm.crop_types_str.split(',').map((s) => s.trim()).filter(Boolean),
      default_water_amount: bedForm.default_water_amount || null,
      notes: bedForm.notes || null,
      size_sqft: bedForm.size_sqft === '' ? null : Number(bedForm.size_sqft),
    };
    try {
      if (editingBed) {
        await axios.put(`${API_URL}/api/garden/beds/${editingBed.id}`, payload, { headers: getHeaders() });
        toast.success('Bed updated');
      } else {
        await axios.post(`${API_URL}/api/garden/beds`, payload, { headers: getHeaders() });
        toast.success('Bed created');
      }
      setShowCreateBed(false);
      await load();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); }
  };

  const deleteBed = async (bed) => {
    if (!window.confirm(`Delete bed "${bed.name}"? Care events + harvests remain but lose their bed link.`)) return;
    try {
      await axios.delete(`${API_URL}/api/garden/beds/${bed.id}`, { headers: getHeaders() });
      toast.success('Bed deleted');
      await load();
    } catch { toast.error('Delete failed'); }
  };

  // One-tap "Watered today" — logs a watering event using the bed's default
  // water amount, then flips the button to "Watered ✓" so double-taps don't
  // create duplicates. Undo restores via the standard 5-second sonner action.
  const logWater = async (bed) => {
    if (wateredToday.has(bed.id)) return;
    // Optimistic flip so the button feels instant.
    setWateredToday((prev) => new Set(prev).add(bed.id));
    try {
      const res = await axios.post(`${API_URL}/api/garden/care-events`, {
        bed_id: bed.id,
        event_type: 'watering',
        amount: bed.default_water_amount || 'Standard watering',
        notes: bed.default_water_amount ? null : 'Standard watering',
        date: todayLocal(),
      }, { headers: getHeaders() });
      const ev = res.data?.event;
      toast.success(`Watered "${bed.name}"`, {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.delete(`${API_URL}/api/garden/care-events/${ev.id}`, { headers: getHeaders() });
              setWateredToday((prev) => { const n = new Set(prev); n.delete(bed.id); return n; });
              toast.success('Undone');
            } catch { toast.error('Undo failed'); }
          },
        },
      });
    } catch (e) {
      // Roll back the optimistic flip on error.
      setWateredToday((prev) => { const n = new Set(prev); n.delete(bed.id); return n; });
      toast.error(errMsg(e, 'Failed to log watering'));
    }
  };

  const waterAll = async () => {
    const targets = beds.filter((b) => (b.status || 'active') === 'active' && !wateredToday.has(b.id));
    if (targets.length === 0) { toast.info('All active beds already watered today'); return; }
    const createdIds = [];
    setWateredToday((prev) => { const n = new Set(prev); targets.forEach((b) => n.add(b.id)); return n; });
    try {
      for (const b of targets) {
        const res = await axios.post(`${API_URL}/api/garden/care-events`, {
          bed_id: b.id,
          event_type: 'watering',
          amount: b.default_water_amount || 'Standard watering',
          notes: b.default_water_amount ? null : 'Standard watering (bulk)',
          date: todayLocal(),
        }, { headers: getHeaders() });
        if (res.data?.event?.id) createdIds.push(res.data.event.id);
      }
      toast.success(`Watered ${targets.length} bed${targets.length === 1 ? '' : 's'}`, {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            for (const id of createdIds) {
              try { await axios.delete(`${API_URL}/api/garden/care-events/${id}`, { headers: getHeaders() }); } catch { /* ignore */ }
            }
            setWateredToday((prev) => { const n = new Set(prev); targets.forEach((b) => n.delete(b.id)); return n; });
            toast.success('Undone');
          },
        },
      });
    } catch (e) {
      // Best-effort rollback — hard to know which succeeded, so just refetch.
      toast.error(errMsg(e, 'Some beds may not have been logged'));
      await load();
    }
  };

  return (
    <div className="space-y-4">
      {/* Beds list with quick watering actions */}
      <Card className="card-farm">
        <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sprout className="w-4 h-4 text-emerald-700" /> Beds
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={waterAll}
              disabled={beds.length === 0}
              data-testid="water-all-beds-btn">
              <Droplets className="w-3.5 h-3.5 mr-1" /> Watered all today
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="new-bed-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New bed
            </Button>
          </div>
        </CardHeader>
        <CardContent data-testid="beds-list">
          {beds.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No beds yet. Create your first bed to start logging watering, care events, and harvests.
            </p>
          ) : (
            <ul className="divide-y">
              {beds.map((b) => (
                <li key={b.id} className="py-3 flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                    <div className="font-medium truncate">{b.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {(b.crop_types || []).slice(0, 4).join(', ') || '—'}
                      {b.default_water_amount ? ` · ${b.default_water_amount}` : ''}
                    </div>
                  </div>
                  {wateredToday.has(b.id) ? (
                    <Button size="sm" variant="ghost" disabled
                      className="text-emerald-700"
                      data-testid={`watered-check-${b.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Watered
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => logWater(b)}
                      data-testid={`water-bed-${b.id}`}>
                      <Droplets className="w-3.5 h-3.5 mr-1" /> Watered today
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => openEdit(b)}
                    data-testid={`edit-bed-${b.id}`}
                    aria-label="Edit bed">
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteBed(b)}
                    data-testid={`delete-bed-${b.id}`}
                    aria-label="Delete bed">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Bed create/edit dialog */}
      <Dialog open={showCreateBed} onOpenChange={(v) => { if (!v) setShowCreateBed(false); }}>
        <DialogContent className="max-w-md" data-testid="bed-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingBed ? 'Edit bed' : 'New bed'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="bed-name">Name</Label>
              <Input id="bed-name" data-testid="bed-name-input"
                value={bedForm.name}
                onChange={(e) => setBedForm({ ...bedForm, name: e.target.value })}
                placeholder="e.g., North raised bed" />
            </div>
            <div>
              <Label htmlFor="bed-crops">Crop types (comma-separated)</Label>
              <Input id="bed-crops"
                value={bedForm.crop_types_str}
                onChange={(e) => setBedForm({ ...bedForm, crop_types_str: e.target.value })}
                placeholder="Tomato, Basil, Marigold" />
            </div>
            <div>
              <Label htmlFor="bed-water">Default watering amount</Label>
              <Input id="bed-water" data-testid="bed-default-water-input"
                value={bedForm.default_water_amount}
                onChange={(e) => setBedForm({ ...bedForm, default_water_amount: e.target.value })}
                placeholder='e.g., "1 gal per plant" or "5 gal drip for 20 min"' />
              <p className="text-[11px] text-muted-foreground mt-1">
                Used when you tap "Watered today". Leave blank to log as "Standard watering".
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="bed-size">Size (sq ft)</Label>
                <Input id="bed-size" type="number" step="0.1"
                  value={bedForm.size_sqft}
                  onChange={(e) => setBedForm({ ...bedForm, size_sqft: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="bed-notes">Notes</Label>
              <Textarea id="bed-notes" rows={2}
                value={bedForm.notes}
                onChange={(e) => setBedForm({ ...bedForm, notes: e.target.value })} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowCreateBed(false)}>Cancel</Button>
              <Button className="flex-1" onClick={saveBed} data-testid="save-bed-btn">
                {editingBed ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Existing 2D visual planner — kept intact so users don't lose that workflow */}
      <Card className="card-farm overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sprout className="w-4 h-4 text-emerald-700" /> Layout planner
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-6 overflow-x-hidden">
          <GardenPlanner />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- LOG TAB ----------------
// General-purpose care event logger — designed so the same shape works for
// post-launch livestock/poultry chore logging (event_type + amount + notes
// are generic; only bed_id ties us to garden).
function LogTab() {
  const getHeaders = useAuthHeaders();
  const [beds, setBeds] = useState([]);
  const [crops, setCrops] = useState([]);
  const [events, setEvents] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [form, setForm] = useState({
    bed_id: '',
    crop_name: '',
    event_type: 'watering',
    amount: '',
    date: todayLocal(),
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [bs, cs, evsRes] = await Promise.all([
      fetchBeds(getHeaders),
      fetchCrops(getHeaders),
      axios.get(`${API_URL}/api/garden/care-events/all?days=180`, { headers: getHeaders() }).catch(() => ({ data: { events: [] } })),
    ]);
    setBeds(bs); setCrops(cs);
    setEvents(evsRes.data?.events || []);
  }, [getHeaders]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.event_type) { toast.error('Pick an event type'); return; }
    const dv = validateDate(form.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/garden/care-events`, {
        bed_id: form.bed_id || null,
        crop_name: form.crop_name || null,
        event_type: form.event_type,
        amount: form.amount || null,
        notes: form.notes || null,
        date: form.date,
        allow_historical: !dv.ok && dv.reason === 'too_old',
      }, { headers: getHeaders() });
      const ev = res.data?.event;
      if (ev) setEvents((prev) => [ev, ...prev]);
      toast.success('Care event logged');
      setForm({ ...form, amount: '', notes: '' });
    } catch (e) { toast.error(errMsg(e, 'Failed to log event')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (ev) => {
    setConfirmDeleteId(null);
    setEvents((prev) => prev.filter((e) => e.id !== ev.id));
    try {
      await axios.delete(`${API_URL}/api/garden/care-events/${ev.id}`, { headers: getHeaders() });
      toast.success('Event deleted', {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(`${API_URL}/api/garden/care-events`, {
                bed_id: ev.bed_id, crop_name: ev.crop_name, event_type: ev.event_type,
                amount: ev.amount, notes: ev.notes, date: ev.date, allow_historical: true,
              }, { headers: getHeaders() });
              toast.success('Restored'); await load();
            } catch { toast.error('Restore failed'); }
          },
        },
      });
    } catch { toast.error('Delete failed'); await load(); }
  };

  const bedName = (id) => beds.find((b) => b.id === id)?.name || '—';

  return (
    <div className="space-y-4">
      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-emerald-700" /> Log care event</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Bed</Label>
              <Select value={form.bed_id || 'none'} onValueChange={(v) => setForm({ ...form, bed_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="care-bed-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No bed —</SelectItem>
                  {beds.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="care-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((e) => (<SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Crop (optional)</Label>
            <CropPicker crops={crops} value={form.crop_name}
              onChange={(v) => setForm({ ...form, crop_name: v })}
              placeholder="Pick a crop or leave blank" dataTestId="care-crop-picker" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="care-date-input" />
            </div>
            <div>
              <Label>Amount / quantity</Label>
              <Input value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder='e.g., "2 gallons compost tea"'
                data-testid="care-amount-input" />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="care-notes-input" />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full" data-testid="care-log-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Log event
          </Button>
        </CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent care events</CardTitle></CardHeader>
        <CardContent data-testid="care-recent-list">
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No care events logged yet.</p>
          ) : (
            <ul className="divide-y">
              {events.slice(0, 40).map((ev) => (
                <li key={ev.id} className="py-2 flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium capitalize truncate">
                      {ev.event_type.replace(/_/g, ' ')}
                      {ev.crop_name ? ` · ${ev.crop_name}` : ''}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {formatDateShort(ev.date)} · {bedName(ev.bed_id)}
                      {ev.amount ? ` · ${ev.amount}` : ''}
                    </div>
                  </div>
                  {confirmDeleteId === ev.id ? (
                    <span className="flex items-center gap-1">
                      <Button size="icon" variant="destructive" className="h-7 w-7"
                        onClick={() => handleDelete(ev)}
                        data-testid={`care-delete-confirm-${ev.id}`}><Check className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7"
                        onClick={() => setConfirmDeleteId(null)}
                        data-testid={`care-delete-cancel-${ev.id}`}><X className="w-3.5 h-3.5" /></Button>
                    </span>
                  ) : (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmDeleteId(ev.id)}
                      data-testid={`care-delete-${ev.id}`}
                      aria-label="Delete event"><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- HARVEST TAB ----------------
function HarvestTab() {
  const getHeaders = useAuthHeaders();
  const [beds, setBeds] = useState([]);
  const [crops, setCrops] = useState([]);
  const [harvests, setHarvests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [form, setForm] = useState({
    bed_id: '', crop_name: '', date: todayLocal(),
    amount: '', unit: 'lbs', quality_notes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [bs, cs, hRes, sRes] = await Promise.all([
      fetchBeds(getHeaders),
      fetchCrops(getHeaders),
      axios.get(`${API_URL}/api/garden/harvests/all?days=365`, { headers: getHeaders() }).catch(() => ({ data: { harvests: [] } })),
      axios.get(`${API_URL}/api/garden/harvest-summary`, { headers: getHeaders() }).catch(() => ({ data: null })),
    ]);
    setBeds(bs); setCrops(cs);
    setHarvests(hRes.data?.harvests || []);
    setSummary(sRes.data || null);
  }, [getHeaders]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.crop_name) { toast.error('Pick a crop'); return; }
    const q = validateQuantity(form.amount, { field: 'Amount', max: HARVEST_UNIT_CAPS[form.unit] });
    if (!q.ok) { toast.error(q.message); return; }
    const dv = validateDate(form.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/garden/harvests`, {
        bed_id: form.bed_id || null,
        crop_name: form.crop_name,
        amount: parseFloat(form.amount),
        unit: form.unit,
        date: form.date,
        quality_notes: form.quality_notes || null,
        allow_historical: !dv.ok && dv.reason === 'too_old',
      }, { headers: getHeaders() });
      const h = res.data?.harvest;
      if (h) setHarvests((prev) => [h, ...prev]);
      toast.success('Harvest logged');
      setForm({ ...form, amount: '', quality_notes: '' });
      // Refresh summary in the background.
      axios.get(`${API_URL}/api/garden/harvest-summary`, { headers: getHeaders() })
        .then((r) => setSummary(r.data)).catch(() => {});
    } catch (e) { toast.error(errMsg(e, 'Failed to log harvest')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (h) => {
    setConfirmDeleteId(null);
    setHarvests((prev) => prev.filter((x) => x.id !== h.id));
    try {
      await axios.delete(`${API_URL}/api/garden/harvests/${h.id}`, { headers: getHeaders() });
      toast.success('Harvest deleted', {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(`${API_URL}/api/garden/harvests`, {
                bed_id: h.bed_id, crop_name: h.crop_name, amount: h.amount, unit: h.unit,
                date: h.date, quality_notes: h.quality_notes, allow_historical: true,
              }, { headers: getHeaders() });
              await load();
              toast.success('Restored');
            } catch { toast.error('Restore failed'); }
          },
        },
      });
      // Refresh summary after 5.2s once undo window closes.
      setTimeout(() => {
        axios.get(`${API_URL}/api/garden/harvest-summary`, { headers: getHeaders() })
          .then((r) => setSummary(r.data)).catch(() => {});
      }, 5200);
    } catch { toast.error('Delete failed'); await load(); }
  };

  return (
    <div className="space-y-4">
      {/* Season summary — mirrors the Livestock Births summary pattern.
          Backend returns total_weight_lbs, total_count, and top_crops[]. */}
      <Card className="card-farm bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Wheat className="w-4 h-4 text-emerald-700" /> Season summary</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3 text-sm" data-testid="harvest-season-summary">
            <SummaryTile label="Total weight" value={summary ? `${summary.total_weight_lbs} lbs` : '—'} />
            <SummaryTile label="Total count" value={summary ? summary.total_count : '—'} />
            <SummaryTile label="Harvests" value={summary ? summary.events : '—'} />
          </div>
          {summary?.top_crops?.length > 0 && (
            <div className="mt-3 text-xs text-muted-foreground">
              Top crops:{' '}
              {summary.top_crops.slice(0, 5).map((c, i) => (
                <span key={c.crop_name}>{i > 0 ? ' · ' : ''}<b>{c.crop_name}</b> {c.total_weight_lbs} lbs</span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Log harvest</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Bed</Label>
              <Select value={form.bed_id || 'none'} onValueChange={(v) => setForm({ ...form, bed_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="harvest-bed-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No bed —</SelectItem>
                  {beds.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="harvest-date-input" />
            </div>
          </div>
          <div>
            <Label>Crop</Label>
            <CropPicker crops={crops} value={form.crop_name}
              onChange={(v) => setForm({ ...form, crop_name: v })}
              placeholder="Pick a crop" dataTestId="harvest-crop-picker" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Amount</Label>
              <Input type="number" step="0.1" min="0" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="harvest-amount-input" />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="harvest-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HARVEST_UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Quality notes (optional)</Label>
            <Input value={form.quality_notes}
              onChange={(e) => setForm({ ...form, quality_notes: e.target.value })}
              placeholder="e.g. 'first flush, best flavor'" />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full" data-testid="harvest-log-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wheat className="w-4 h-4 mr-2" />} Log harvest
          </Button>
        </CardContent>
      </Card>

      <Card className="card-farm">
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent harvests</CardTitle></CardHeader>
        <CardContent data-testid="harvest-recent-list">
          {harvests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No harvests logged yet.</p>
          ) : (
            <ul className="divide-y">
              {harvests.slice(0, 40).map((h) => (
                <li key={h.id} className="py-2 flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{h.crop_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {formatDateShort(h.date)} · {beds.find((b) => b.id === h.bed_id)?.name || '—'}
                      {h.quality_notes ? ` · ${h.quality_notes}` : ''}
                    </div>
                  </div>
                  <div className="font-mono font-semibold text-right shrink-0">
                    {h.amount} {h.unit}
                  </div>
                  {confirmDeleteId === h.id ? (
                    <span className="flex items-center gap-1">
                      <Button size="icon" variant="destructive" className="h-7 w-7"
                        onClick={() => handleDelete(h)}
                        data-testid={`harvest-delete-confirm-${h.id}`}><Check className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7"
                        onClick={() => setConfirmDeleteId(null)}
                        data-testid={`harvest-delete-cancel-${h.id}`}><X className="w-3.5 h-3.5" /></Button>
                    </span>
                  ) : (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmDeleteId(h.id)}
                      data-testid={`harvest-delete-${h.id}`}
                      aria-label="Delete harvest"><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold text-emerald-800">{value}</div>
    </div>
  );
}
